import { parse } from '@babel/parser';
import traverse from '@babel/traverse';
import generate from '@babel/generator';
import * as t from '@babel/types';
import { readFileSync, writeFileSync } from 'fs';
import { hasVibeshipProvider } from './layout-detector';
import type { LayoutInfo } from './layout-detector';

export interface InjectionResult {
  success: boolean;
  message: string;
  modifiedContent?: string;
}

/**
 * Injects VibeshipProvider into a layout file
 */
export async function injectProvider(
  layoutInfo: LayoutInfo,
  configPath: string = './vibeship.config'
): Promise<InjectionResult> {
  try {
    if (!layoutInfo.exists) {
      // Create new layout file
      const content = layoutInfo.isAppRouter
        ? createAppRouterLayout(layoutInfo.isTypeScript, configPath)
        : createPagesRouterApp(layoutInfo.isTypeScript, configPath);
      
      return {
        success: true,
        message: 'Created new layout file with VibeshipProvider',
        modifiedContent: content,
      };
    }

    // Read existing file
    const content = readFileSync(layoutInfo.path, 'utf-8');
    
    // Check if provider already exists
    if (hasVibeshipProvider(content)) {
      return {
        success: true,
        message: 'VibeshipProvider already exists in layout file',
      };
    }

    // Inject into existing file
    const result = layoutInfo.isAppRouter
      ? await injectIntoAppRouter(content, layoutInfo.isTypeScript, configPath)
      : await injectIntoPagesRouter(content, layoutInfo.isTypeScript, configPath);

    return result;
  } catch (error) {
    return {
      success: false,
      message: `Failed to inject provider: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Creates a new App Router layout with VibeshipProvider
 */
function createAppRouterLayout(isTypeScript: boolean, configPath: string): string {
  const importPath = configPath.replace(/\.(ts|js)$/, '');
  
  if (isTypeScript) {
    return `import { VibeshipServerProvider } from '@vibeship/devtools'
import vibeshipConfig from '${importPath}'
import '@vibeship/ui/styles'

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang="en">
      <body>
        <VibeshipServerProvider config={vibeshipConfig}>
          {children}
        </VibeshipServerProvider>
      </body>
    </html>
  )
}
`;
  } else {
    return `import { VibeshipServerProvider } from '@vibeship/devtools'
import vibeshipConfig from '${importPath}'
import '@vibeship/ui/styles'

export default function RootLayout({ children }) {
  return (
    <html lang="en">
      <body>
        <VibeshipServerProvider config={vibeshipConfig}>
          {children}
        </VibeshipServerProvider>
      </body>
    </html>
  )
}
`;
  }
}

/**
 * Creates a new Pages Router _app with VibeshipProvider
 */
function createPagesRouterApp(isTypeScript: boolean, configPath: string): string {
  const importPath = configPath.replace(/\.(ts|js)$/, '');
  
  if (isTypeScript) {
    return `import { VibeshipServerProvider } from '@vibeship/devtools'
import vibeshipConfig from '${importPath}'
import '@vibeship/ui/styles'
import type { AppProps } from 'next/app'

export default function App({ Component, pageProps }: AppProps) {
  return (
    <VibeshipServerProvider config={vibeshipConfig}>
      <Component {...pageProps} />
    </VibeshipServerProvider>
  )
}
`;
  } else {
    return `import { VibeshipServerProvider } from '@vibeship/devtools'
import vibeshipConfig from '${importPath}'
import '@vibeship/ui/styles'

export default function App({ Component, pageProps }) {
  return (
    <VibeshipServerProvider config={vibeshipConfig}>
      <Component {...pageProps} />
    </VibeshipServerProvider>
  )
}
`;
  }
}

/**
 * Injects provider into existing App Router layout
 */
async function injectIntoAppRouter(
  content: string,
  isTypeScript: boolean,
  configPath: string
): Promise<InjectionResult> {
  try {
    const ast = parse(content, {
      sourceType: 'module',
      plugins: ['jsx', 'typescript'] as any[],
    });

    let modified = false;
    let hasImports = false;

    // Add imports
    const importPath = configPath.replace(/\.(ts|js)$/, '');
    const vibeshipImport = t.importDeclaration(
      [
        t.importSpecifier(
          t.identifier('VibeshipServerProvider'),
          t.identifier('VibeshipServerProvider')
        ),
      ],
      t.stringLiteral('@vibeship/devtools')
    );

    const configImport = t.importDeclaration(
      [
        t.importDefaultSpecifier(t.identifier('vibeshipConfig')),
      ],
      t.stringLiteral(importPath)
    );
    
    const stylesImport = t.importDeclaration(
      [],
      t.stringLiteral('@vibeship/ui/styles')
    );

    // Find where to insert imports
    let lastImportIndex = -1;
    ast.program.body.forEach((node, index) => {
      if (t.isImportDeclaration(node)) {
        lastImportIndex = index;
      }
    });

    // Insert imports after last import
    if (lastImportIndex >= 0) {
      ast.program.body.splice(lastImportIndex + 1, 0, vibeshipImport, configImport, stylesImport);
    } else {
      ast.program.body.unshift(vibeshipImport, configImport, stylesImport);
    }


    // Find and wrap the layout component
    traverse(ast, {
      // Handle default export function
      ExportDefaultDeclaration(path) {
        const declaration = path.node.declaration;
        
        if (t.isFunctionDeclaration(declaration) && declaration.id?.name === 'RootLayout') {
          wrapLayoutBody(declaration);
          modified = true;
        }
      },
      
      // Handle named function that's exported
      FunctionDeclaration(path) {
        if (path.node.id?.name === 'RootLayout' && !modified) {
          wrapLayoutBody(path.node);
          modified = true;
        }
      },
      
      // Handle arrow function assigned to variable
      VariableDeclarator(path) {
        if (t.isIdentifier(path.node.id) && 
            path.node.id.name === 'RootLayout' && 
            t.isArrowFunctionExpression(path.node.init)) {
          wrapLayoutBody(path.node.init as any);
          modified = true;
        }
      },
    });

    if (!modified) {
      return {
        success: false,
        message: 'Could not find RootLayout component to wrap',
      };
    }

    const { code } = generate(ast, {
      retainLines: false,
      compact: false,
    });

    return {
      success: true,
      message: 'Successfully injected VibeshipProvider',
      modifiedContent: code,
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to parse/modify layout: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Injects provider into existing Pages Router _app
 */
async function injectIntoPagesRouter(
  content: string,
  isTypeScript: boolean,
  configPath: string
): Promise<InjectionResult> {
  try {
    const ast = parse(content, {
      sourceType: 'module',
      plugins: ['jsx', 'typescript'] as any[],
    });

    let modified = false;

    // Add imports
    const importPath = configPath.replace(/\.(ts|js)$/, '');
    const vibeshipImport = t.importDeclaration(
      [
        t.importSpecifier(
          t.identifier('VibeshipServerProvider'),
          t.identifier('VibeshipServerProvider')
        ),
      ],
      t.stringLiteral('@vibeship/devtools')
    );

    const configImport = t.importDeclaration(
      [
        t.importDefaultSpecifier(t.identifier('vibeshipConfig')),
      ],
      t.stringLiteral(importPath)
    );
    
    const stylesImport = t.importDeclaration(
      [],
      t.stringLiteral('@vibeship/ui/styles')
    );

    // Find where to insert imports
    let lastImportIndex = -1;
    ast.program.body.forEach((node, index) => {
      if (t.isImportDeclaration(node)) {
        lastImportIndex = index;
      }
    });

    // Insert imports
    if (lastImportIndex >= 0) {
      ast.program.body.splice(lastImportIndex + 1, 0, vibeshipImport, configImport, stylesImport);
    } else {
      ast.program.body.unshift(vibeshipImport, configImport, stylesImport);
    }

    // Find and wrap the App component
    traverse(ast, {
      // Handle default export function
      ExportDefaultDeclaration(path) {
        const declaration = path.node.declaration;
        
        if (t.isFunctionDeclaration(declaration) && 
            (declaration.id?.name === 'App' || declaration.id?.name === 'MyApp')) {
          wrapAppReturn(declaration);
          modified = true;
        }
      },
      
      // Handle arrow function or regular function
      VariableDeclarator(path) {
        if (t.isIdentifier(path.node.id) && 
            (path.node.id.name === 'App' || path.node.id.name === 'MyApp')) {
          const init = path.node.init;
          if (t.isArrowFunctionExpression(init) || t.isFunctionExpression(init)) {
            wrapAppReturn(init as any);
            modified = true;
          }
        }
      },
    });

    if (!modified) {
      return {
        success: false,
        message: 'Could not find App component to wrap',
      };
    }

    const { code } = generate(ast, {
      retainLines: false,
      compact: false,
    });

    return {
      success: true,
      message: 'Successfully injected VibeshipProvider',
      modifiedContent: code,
    };
  } catch (error) {
    return {
      success: false,
      message: `Failed to parse/modify _app: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

/**
 * Helper to wrap layout body with provider
 */
function wrapLayoutBody(func: t.FunctionDeclaration | t.ArrowFunctionExpression | t.FunctionExpression) {
  const file = t.file(t.program([func as any]));
  traverse(file, {
    JSXElement(path) {
      // Find the body element
      if (t.isJSXIdentifier(path.node.openingElement.name) &&
          path.node.openingElement.name.name === 'body') {
        
        // Get existing children
        const existingChildren = path.node.children;
        
        // Create provider wrapper
        const provider = t.jsxElement(
          t.jsxOpeningElement(
            t.jsxIdentifier('VibeshipServerProvider'),
            [
              t.jsxAttribute(
                t.jsxIdentifier('config'),
                t.jsxExpressionContainer(t.identifier('vibeshipConfig'))
              ),
            ],
            false
          ),
          t.jsxClosingElement(t.jsxIdentifier('VibeshipServerProvider')),
          [
            ...existingChildren,
          ],
          false
        );
        
        // Replace body children with wrapped content
        path.node.children = [
          t.jsxText('\n        '),
          provider,
          t.jsxText('\n      '),
        ];
        
        path.stop();
      }
    },
  });
}

/**
 * Helper to wrap _app return with provider
 */
function wrapAppReturn(func: t.FunctionDeclaration | t.ArrowFunctionExpression | t.FunctionExpression) {
  const file = t.file(t.program([func as any]));
  traverse(file, {
    ReturnStatement(path) {
      const argument = path.node.argument;
      
      if (t.isJSXElement(argument) || t.isJSXFragment(argument)) {
        // Wrap the return value with provider
        const provider = t.jsxElement(
          t.jsxOpeningElement(
            t.jsxIdentifier('VibeshipServerProvider'),
            [
              t.jsxAttribute(
                t.jsxIdentifier('config'),
                t.jsxExpressionContainer(t.identifier('vibeshipConfig'))
              ),
            ],
            false
          ),
          t.jsxClosingElement(t.jsxIdentifier('VibeshipServerProvider')),
          [
            t.jsxText('\n      '),
            argument,
            t.jsxText('\n    '),
          ],
          false
        );
        
        path.node.argument = provider;
        path.stop();
      }
    },
  });
}